import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Branding from './Branding';
import posthog from '../utils/posthog-stub'; // FERPA: Using no-op stub until self-hosted PostHog is configured

const EmailVerification = () => {
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Extract token from URL query params
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Verification token is missing');
      return;
    }
    
    // Call API to verify token
    axios.get(`/api/auth/verify-token?token=${token}`)
      .then(response => {
        if (response.data.success) {
          setStatus('success');
          setMessage(response.data.message);
          setEmail(response.data.email);
          
          // Update localStorage to add 15 minutes to free time
          try {
            // Get current free minutes or set default
            const currentFreeMinutes = parseInt(localStorage.getItem('morpheus_free_minutes') || '30');
            // Add 15 minutes for verification
            localStorage.setItem('morpheus_free_minutes', (currentFreeMinutes + 15).toString());
            
            // Store the email verification status
            localStorage.setItem('morpheus_email_verified', 'true');
            localStorage.setItem('morpheus_verified_email', response.data.email);
            
            // Link with anonymous ID if available
            if (response.data.anonymous_id) {
              localStorage.setItem('morpheus_anon_id', response.data.anonymous_id);
            }
            
            // Track in PostHog
            try {
              posthog.capture('email_verified', {
                email: response.data.email,
                anonymous_id: response.data.anonymous_id,
                verification_time: new Date().toISOString()
              });
            } catch (err) {
              console.error('PostHog tracking error:', err);
            }
          } catch (err) {
            console.error('Error updating localStorage:', err);
          }
        } else {
          setStatus('error');
          setMessage(response.data.error || 'Email verification failed');
        }
      })
      .catch(error => {
        setStatus('error');
        setMessage(error.response?.data?.error || 'An error occurred during verification');
        console.error('Email verification error:', error);
      });
  }, [location.search]);
  
  const handleContinue = () => {
    navigate('/');
  };
  
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: Branding.gradients.headerGradient,
      padding: Branding.spacing.md
    }}>
      <div style={{
        backgroundColor: Branding.colors.pureWhite,
        borderRadius: Branding.borders.radius.lg,
        boxShadow: Branding.shadows.lg,
        padding: Branding.spacing.xl,
        width: '100%',
        maxWidth: '500px',
        textAlign: 'center'
      }}>
        {status === 'verifying' && (
          <>
            <div style={{
              marginBottom: Branding.spacing.lg,
              display: 'inline-block',
              width: '48px',
              height: '48px',
              border: `4px solid ${Branding.colors.paleBlue}`,
              borderTopColor: Branding.colors.tealBlue,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <h2 style={{
              color: Branding.colors.deepPurple,
              marginBottom: Branding.spacing.sm,
              fontWeight: Branding.typography.fontWeight.bold,
              fontSize: Branding.typography.fontSize.xl
            }}>Verifying Your Email</h2>
            <p style={{
              color: Branding.colors.slateGray,
              fontSize: Branding.typography.fontSize.base
            }}>Please wait while we verify your email address...</p>
            
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div style={{ 
              color: Branding.colors.emeraldGreen,
              fontSize: '48px',
              marginBottom: Branding.spacing.md
            }}>✓</div>
            <h2 style={{
              color: Branding.colors.deepPurple,
              marginBottom: Branding.spacing.sm,
              fontWeight: Branding.typography.fontWeight.bold,
              fontSize: Branding.typography.fontSize.xl
            }}>Email Verified!</h2>
            <p style={{
              color: Branding.colors.slateGray,
              fontSize: Branding.typography.fontSize.base,
              marginBottom: Branding.spacing.md
            }}>{message}</p>
            
            <div style={{
              backgroundColor: Branding.colors.paleGreen,
              border: `1px solid ${Branding.colors.emeraldGreen}`,
              borderRadius: Branding.borders.radius.md,
              padding: Branding.spacing.md,
              marginBottom: Branding.spacing.lg
            }}>
              <h3 style={{
                color: Branding.colors.emeraldGreen,
                fontWeight: Branding.typography.fontWeight.semibold,
                fontSize: Branding.typography.fontSize.base,
                marginBottom: Branding.spacing.xs
              }}>Your Benefits</h3>
              <p style={{
                color: Branding.colors.darkSlate,
                fontSize: Branding.typography.fontSize.sm,
                marginBottom: Branding.spacing.xs
              }}>✓ <strong>15 additional free minutes</strong> of tutoring</p>
              <p style={{
                color: Branding.colors.darkSlate,
                fontSize: Branding.typography.fontSize.sm
              }}>✓ <strong>Email-linked sessions</strong> for better continuity</p>
            </div>
            
            <button
              onClick={handleContinue}
              style={{
                padding: Branding.spacing.md,
                background: Branding.gradients.warmGradient,
                color: Branding.colors.pureWhite,
                border: 'none',
                borderRadius: Branding.borders.radius.md,
                fontSize: Branding.typography.fontSize.base,
                fontWeight: Branding.typography.fontWeight.semibold,
                cursor: 'pointer',
                width: '100%',
                maxWidth: '200px',
                transition: Branding.transitions.default
              }}
            >
              Continue to Tutor
            </button>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div style={{ 
              color: Branding.colors.errorRed,
              fontSize: '48px',
              marginBottom: Branding.spacing.md
            }}>✗</div>
            <h2 style={{
              color: Branding.colors.deepPurple,
              marginBottom: Branding.spacing.sm,
              fontWeight: Branding.typography.fontWeight.bold,
              fontSize: Branding.typography.fontSize.xl
            }}>Verification Failed</h2>
            <p style={{
              color: Branding.colors.slateGray,
              fontSize: Branding.typography.fontSize.base,
              marginBottom: Branding.spacing.lg
            }}>{message}</p>
            
            <button
              onClick={handleContinue}
              style={{
                padding: Branding.spacing.md,
                background: Branding.gradients.paleGradient,
                color: Branding.colors.tealBlue,
                border: `1px solid ${Branding.colors.tealBlue}`,
                borderRadius: Branding.borders.radius.md,
                fontSize: Branding.typography.fontSize.base,
                fontWeight: Branding.typography.fontWeight.semibold,
                cursor: 'pointer',
                width: '100%',
                maxWidth: '200px',
                transition: Branding.transitions.default
              }}
            >
              Return to Tutor
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailVerification;